/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  LinearProgress,
  Snackbar,
  Alert,
  IconButton,
  Paper,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Tooltip
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import DeleteIcon from "@mui/icons-material/Delete";

const API_BASE_URL = process.env.REACT_APP_API_URL;
console.log(API_BASE_URL);

export default function BannerControl() {
  // Get user permissions from localStorage
  const userPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const canRead = userPermissions.includes('read_Banner');
  const canCreate = userPermissions.includes('create_Banner');
  const canUpdate = userPermissions.includes('update_Banner');
  const canDelete = userPermissions.includes('delete_Banner');
  const canToggle = userPermissions.includes('update_Banner');

  const [banners, setBanners] = useState([]);
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [activeCount, setActiveCount] = useState(0);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [bannerToActivate, setBannerToActivate] = useState(null);

  const MAX_ACTIVE_BANNERS = 5;
  const MAX_FILE_SIZE = 200 * 1024; // 200KB in bytes

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      showSnackbar("You are not logged in", "error");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchBanners = async () => {
    if (!canRead) {
      showSnackbar("You don't have permission to view banners", "error");
      return;
    }

    const headers = getAuthHeader();
    if (!headers) return;

    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/banners/my`, { headers });
      setBanners(data);
      setActiveCount(data.filter(b => b.isActive).length);
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to fetch banners: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          reject(`Image size must be less than ${MAX_FILE_SIZE/1024}KB. Your image is ${(file.size/1024).toFixed(2)}KB.`);
          return;
        }
        
        resolve(true);
        URL.revokeObjectURL(url);
      };
      
      img.onerror = () => {
        reject("Invalid image file");
        URL.revokeObjectURL(url);
      };
      
      img.src = url;
    });
  };

  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      setFiles(null);
      return;
    }

    const validFiles = [];
    const invalidFiles = [];

    // Validate each file
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      try {
        await validateImage(file);
        validFiles.push(file);
      } catch (error) {
        invalidFiles.push({ file, error });
      }
    }

    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles.map(f => 
        `${f.file.name}: ${f.error}`
      ).join('\n');
      showSnackbar(`Some files were invalid:\n${errorMessages}`, "error");
    }

    if (validFiles.length > 0) {
      setFiles(Array.from(validFiles));
    } else {
      setFiles(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!canCreate) {
      showSnackbar("You don't have permission to upload banners", "error");
      return;
    }

    const headers = getAuthHeader();
    if (!headers) return;
    if (!files || files.length === 0) {
      showSnackbar("Please select at least one valid image", "warning");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    for (let file of files) formData.append("banners", file);

    try {
      await axios.post(`${API_BASE_URL}/api/banners/upload`, formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      });
      showSnackbar("Banners uploaded successfully!");
      fetchBanners();
      setFiles(null);
    } catch (error) {
      console.error(error);
      showSnackbar("Upload failed: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setUploading(false);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    if (!canToggle) {
      showSnackbar("You don't have permission to toggle banner status", "error");
      return;
    }

    if (currentStatus === false && activeCount >= MAX_ACTIVE_BANNERS) {
      setBannerToActivate(id);
      setShowLimitDialog(true);
      return;
    }

    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await axios.patch(`${API_BASE_URL}/api/banners/toggle/${id}`, {}, { headers });
      showSnackbar(`Banner ${currentStatus ? "deactivated" : "activated"}`);
      fetchBanners();
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to toggle status", "error");
    }
  };

  const handleDeactivateAndActivate = async (deactivateId) => {
    if (!canToggle) {
      showSnackbar("You don't have permission to toggle banner status", "error");
      return;
    }

    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await axios.patch(`${API_BASE_URL}/api/banners/toggle/${deactivateId}`, {}, { headers });
      await axios.patch(`${API_BASE_URL}/api/banners/toggle/${bannerToActivate}`, {}, { headers });
      showSnackbar("Banners updated successfully");
      fetchBanners();
      setShowLimitDialog(false);
      setBannerToActivate(null);
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to update banners", "error");
    }
  };

  const deleteBanner = async (id) => {
    if (!canDelete) {
      showSnackbar("You don't have permission to delete banners", "error");
      return;
    }

    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/banners/${id}`, { headers });
      showSnackbar("Banner deleted successfully");
      fetchBanners();
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to delete banner", "error");
    }
  };

  useEffect(() => {
    if (canRead) {
      fetchBanners();
    } else {
      showSnackbar("You don't have permission to view banners", "error");
    }
  }, []);

  const activeBanners = banners.filter(banner => banner.isActive);

  if (!canRead) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            You don't have permission to view this page
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Banner Management
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Chip 
            label={`${activeCount}/${MAX_ACTIVE_BANNERS} active`} 
            color={activeCount >= MAX_ACTIVE_BANNERS ? "error" : "success"}
            variant="outlined"
            sx={{ mr: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            Only active banners will be displayed on your store
          </Typography>
        </Box>

        <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Image Requirements:</strong>
            <ul>
              <li>Maximum file size: {MAX_FILE_SIZE/1024}KB</li>
              <li>Accepted formats: JPEG, PNG, GIF</li>
            </ul>
          </Typography>
        </Box>

        {canCreate ? (
          <form onSubmit={handleUpload}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="banner-upload"
              multiple
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="banner-upload">
              <Button
                component="span"
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                sx={{ mr: 2 }}
              >
                Select Images
              </Button>
            </label>
            
            {files && files.length > 0 && (
              <Typography variant="body2" sx={{ display: 'inline', ml: 2 }}>
                {files.length} valid file(s) selected
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={!files || uploading}
              sx={{ mt: { xs: 2, sm: 0 }, ml: { sm: 2 } }}
            >
              {uploading ? "Uploading..." : "Upload Banners"}
            </Button>
          </form>
        ) : (
          <Typography variant="body2" color="text.secondary">
            You don't have permission to upload new banners
          </Typography>
        )}
      </Paper>

      {loading ? (
        <LinearProgress />
      ) : banners.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6">No banners uploaded yet</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {canCreate ? "Upload images to showcase your products and promotions" : "No banners available"}
          </Typography>
        </Paper>
      ) : (
        <div className="banners-container">
          <Grid container spacing={3}>
            {banners.map((banner) => (
              <Grid item xs={12} sm={6} md={4} key={banner._id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: banner.isActive ? "2px solid #4caf50" : "1px solid #e0e0e0"
                  }}
                >
                  <CardMedia
                    component="img"
                    image={`${API_BASE_URL}/uploads/${banner.image}`}
                    alt="Banner"
                    sx={{
                      height: 140,
                      objectFit: "cover"
                    }}
                  />
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Chip
                        label={banner.isActive ? "Active" : "Inactive"}
                        color={banner.isActive ? "success" : "default"}
                        size="small"
                      />
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "space-between", mt: "auto" }}>
                    {canToggle ? (
                      <Button
                        size="small"
                        startIcon={banner.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        onClick={() => toggleStatus(banner._id, banner.isActive)}
                        color={banner.isActive ? "success" : "inherit"}
                      >
                        {banner.isActive ? "Active" : "Inactive"}
                      </Button>
                    ) : (
                      <Tooltip title="No permission to toggle status">
                        <span>
                          <Button
                            size="small"
                            startIcon={banner.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                            disabled
                          >
                            {banner.isActive ? "Active" : "Inactive"}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    
                    {canDelete ? (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteBanner(banner._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : (
                      <Tooltip title="No permission to delete">
                        <span>
                          <IconButton size="small" color="error" disabled>
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </div>
      )}

      {/* Dialog for selecting which banner to deactivate */}
      <Dialog
        open={showLimitDialog}
        onClose={() => setShowLimitDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Maximum Active Banners Reached
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You can only have {MAX_ACTIVE_BANNERS} active banners at a time. 
            Please select one to deactivate:
          </DialogContentText>
          <List>
            {activeBanners.map((banner) => (
              <ListItem 
                key={banner._id} 
                button 
                onClick={() => handleDeactivateAndActivate(banner._id)}
              >
                <ListItemAvatar>
                  <Avatar 
                    src={`${API_BASE_URL}/uploads/${banner.image}`} 
                    variant="square"
                  />
                </ListItemAvatar>
                <ListItemText primary={`Banner ${banners.findIndex(b => b._id === banner._id) + 1}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLimitDialog(false)} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
